import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Empty, Form, Input, Skeleton, Space, Spin, Tabs, Tag } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Archive, ArrowLeft, History, Save, Send } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { componentsApi } from '@api/components';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { COMPONENT_CONSTRAINTS, type PropType } from '../constants';
import type { ComponentDetail, ComponentStatus, PropFieldModel } from '../types';
import {
  createProp,
  parsePropsSchema,
  serializePropsSchema,
  validateComponent,
} from '../utils/schema';
import {
  componentJsonToPuckData,
  legacyTemplateToPuckData,
  prettyPrintJson,
  puckDataToComponentJson,
  type PuckStudioData,
} from '../blocks/serialization';
import { parseDefaultStyle } from '../blocks/defaultStyle';
import { PropPalette } from '../components/PropPalette';
import { PropList } from '../components/PropList';
import { PropPropertiesPanel } from '../components/PropPropertiesPanel';
import { PlaceholderChips } from '../components/PlaceholderChips';
import { ComponentUsagesPanel } from '../components/ComponentUsagesPanel';
import { ComponentVersionsDrawer } from '../components/ComponentVersionsDrawer';

// K-27 — Puck runtime + CSS'i yalnız burada, lazy `vendor-puck` chunk'ında çek.
const StudioCanvas = lazy(() => import('../studio/StudioCanvas'));

interface EditorDraft {
  name: string;
  category: string;
  props: PropFieldModel[];
  puckData: PuckStudioData;
  defaultStyleJson: string;
}

const STATUS_COLOR: Record<ComponentStatus, string> = {
  Draft: 'default',
  Published: 'green',
  Archived: 'orange',
};

function toDraft(c: ComponentDetail): EditorDraft {
  // K-18/K-20 — componentJson varsa Puck ağacı; yoksa legacy templateHtml köprüsü.
  const puckData = c.componentJson
    ? componentJsonToPuckData(c.componentJson)
    : legacyTemplateToPuckData(c.templateHtml);
  return {
    name: c.name,
    category: c.category,
    props: parsePropsSchema(c.propsSchemaJson),
    puckData,
    defaultStyleJson: c.defaultStyleJson,
  };
}

// Puck id'leri her yüklemede değiştiği için imza id-bağımsız (canonical JSON) alınır.
function signature(draft: EditorDraft): string {
  return JSON.stringify({
    name: draft.name.trim(),
    category: draft.category.trim(),
    props: serializePropsSchema(draft.props),
    componentJson: puckDataToComponentJson(draft.puckData),
    defaultStyleJson: draft.defaultStyleJson.trim(),
  });
}

export function ComponentEditorPage() {
  const { t } = useTranslation(['component-studio', 'common']);
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.COMPONENT_MANAGE);

  const { data: component, isLoading } = useQuery({
    queryKey: ['component', id],
    queryFn: () => componentsApi.get(id),
    enabled: !!id,
  });

  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const openedRef = useRef<string | null>(null);

  // İlk yüklemede (veya id/refetch değişince) draft'a al; publish draft'ı ezmez.
  useEffect(() => {
    if (component && openedRef.current !== component.id) {
      openedRef.current = component.id;
      setDraft(toDraft(component));
      setSelectedId(null);
    }
  }, [component]);

  const validationErrors = useMemo(
    () =>
      draft
        ? // templateHtml editörde düzenlenmez (K-20); yalnız ad/kategori/prop şeması denetlenir.
          validateComponent({
            name: draft.name,
            category: draft.category,
            fields: draft.props,
            templateHtml: '',
          })
        : [],
    [draft],
  );
  const invalidPropIds = useMemo(
    () => new Set(validationErrors.map((e) => e.fieldId).filter((v): v is string => v !== null)),
    [validationErrors],
  );
  const errorMessages = useMemo(() => {
    const tr = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
    return Array.from(
      new Set(validationErrors.map((e) => tr(`builder.errors.${e.code}`, { value: e.value }))),
    );
  }, [validationErrors, t]);

  const isDirty = useMemo(
    () => !!draft && !!component && signature(draft) !== signature(toDraft(component)),
    [draft, component],
  );
  const canSave = canManage && !!draft && validationErrors.length === 0 && isDirty;

  const wrapperStyle = useMemo(
    () => (draft ? parseDefaultStyle(draft.defaultStyleJson) : {}),
    [draft],
  );
  const currentSnapshot = useMemo(
    () => (draft ? prettyPrintJson(puckDataToComponentJson(draft.puckData)) : ''),
    [draft],
  );

  const invalidateAll = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['component', id] }),
      qc.invalidateQueries({ queryKey: ['components'] }),
      qc.invalidateQueries({ queryKey: ['component-palette'] }),
    ]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft || !component) throw new Error('no draft');
      return componentsApi.update(id, {
        name: draft.name.trim(),
        category: draft.category.trim(),
        propsSchemaJson: serializePropsSchema(draft.props),
        // K-20 — legacy fallback bozulmasın: mevcut templateHtml değişmeden geri gönderilir.
        templateHtml: component.templateHtml,
        componentJson: puckDataToComponentJson(draft.puckData),
        defaultStyleJson: draft.defaultStyleJson,
      });
    },
    onSuccess: async (saved) => {
      openedRef.current = '';
      setDraft(toDraft(saved));
      setSelectedId(null);
      toast.success(t('editor.saved'));
      await invalidateAll();
      await qc.invalidateQueries({ queryKey: ['component-versions', id] });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: () => componentsApi.publish(id),
    onSuccess: async () => {
      toast.success(t('toast.published'));
      await invalidateAll();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const archiveMutation = useMutation({
    mutationFn: () => componentsApi.archive(id),
    onSuccess: async () => {
      toast.success(t('toast.archived'));
      await invalidateAll();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMeta = (patch: Partial<EditorDraft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const addProp = (type: PropType) => {
    const prop = createProp(type, { label: t(`builder.propType.${type}`) });
    setDraft((d) => (d ? { ...d, props: [...d.props, prop] } : d));
    setSelectedId(prop.id);
  };
  const updateProp = (propId: string, patch: Partial<PropFieldModel>) =>
    setDraft((d) =>
      d ? { ...d, props: d.props.map((p) => (p.id === propId ? { ...p, ...patch } : p)) } : d,
    );
  const removeProp = (propId: string) => {
    setDraft((d) => (d ? { ...d, props: d.props.filter((p) => p.id !== propId) } : d));
    setSelectedId((cur) => (cur === propId ? null : cur));
  };
  const reorderProps = (next: PropFieldModel[]) => setDraft((d) => (d ? { ...d, props: next } : d));

  const selectedProp = draft?.props.find((p) => p.id === selectedId) ?? null;

  if (isLoading || (!draft && component)) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (!component || !draft) {
    return (
      <Card>
        <Empty description={t('editor.notFound')}>
          <Button onClick={() => void navigate(ROUTES.COMPONENTS)}>{t('editor.back')}</Button>
        </Empty>
      </Card>
    );
  }

  return (
    <div data-testid="component-editor">
      <PageHeader
        title={component.name || t('title')}
        description={t('description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('title'), to: ROUTES.COMPONENTS },
          { label: component.name || '…' },
        ]}
        actions={
          <Space wrap>
            <Button icon={<ArrowLeft size={15} />} onClick={() => void navigate(ROUTES.COMPONENTS)}>
              {t('editor.back')}
            </Button>
            <Button icon={<History size={15} />} onClick={() => setVersionsOpen(true)}>
              {t('versions.open')}
            </Button>
            <Tag color={STATUS_COLOR[component.status]}>{t(`status.${component.status}`)}</Tag>
            {canManage && component.status !== 'Published' ? (
              <Button
                icon={<Send size={15} />}
                onClick={() => publishMutation.mutate()}
                loading={publishMutation.isPending}
                data-testid="component-publish"
              >
                {t('status.Published')}
              </Button>
            ) : null}
            {canManage && component.status !== 'Archived' ? (
              <Button
                icon={<Archive size={15} />}
                onClick={() => archiveMutation.mutate()}
                loading={archiveMutation.isPending}
                data-testid="component-archive"
              >
                {t('status.Archived')}
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<Save size={15} />}
              disabled={!canSave}
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              data-testid="component-save"
            >
              {t('editor.save')}
            </Button>
          </Space>
        }
      />

      {errorMessages.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('builder.errors.title')}
          description={
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {errorMessages.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          }
          data-testid="component-errors"
        />
      ) : null}

      {/* K-30 — kompakt meta satırı (her sekmede görünür): ad her zaman erişilebilir. */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form layout="vertical" disabled={!canManage} style={{ marginBottom: 0 }}>
          <Form.Item label={t('meta.name')} required style={{ marginBottom: 0 }}>
            <Input
              value={draft.name}
              maxLength={COMPONENT_CONSTRAINTS.maxNameLength}
              onChange={(e) => updateMeta({ name: e.target.value })}
              data-testid="component-name"
            />
          </Form.Item>
        </Form>
      </Card>

      {/* K-30 — sekmeli yerleşim: Tasarım (tam genişlik canvas) · Proplar · Ayarlar.
          Draft state sekmelerin ÜZERİNDE tutulur → sekme değişimi taslağı kaybetmez. */}
      <Tabs
        defaultActiveKey="design"
        items={[
          {
            key: 'design',
            label: t('editor.tabs.design'),
            children: (
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <div className="cs-studio-frame" style={wrapperStyle}>
                  <Suspense
                    fallback={
                      <div style={{ padding: 48, textAlign: 'center' }}>
                        <Spin />
                        <div style={{ marginTop: 12, color: 'var(--ds-text-secondary)' }}>
                          {t('editor.canvasLoading')}
                        </div>
                      </div>
                    }
                  >
                    <StudioCanvas
                      data={draft.puckData}
                      onChange={(puckData) => setDraft((d) => (d ? { ...d, puckData } : d))}
                      externalProps={draft.props}
                    />
                  </Suspense>
                </div>
              </Card>
            ),
          },
          {
            key: 'props',
            label: t('editor.tabs.props'),
            children: (
              <Card size="small" title={t('editor.propsAdvanced')}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <PlaceholderChips props={draft.props} />
                  <PropPalette onAdd={addProp} disabled={!canManage} />
                  <PropList
                    props={draft.props}
                    selectedId={selectedId}
                    invalidIds={invalidPropIds}
                    onSelect={setSelectedId}
                    onReorder={reorderProps}
                    onRemove={removeProp}
                    disabled={!canManage}
                  />
                  <PropPropertiesPanel
                    prop={selectedProp}
                    onChange={(patch) => selectedProp && updateProp(selectedProp.id, patch)}
                    disabled={!canManage}
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: 'settings',
            label: t('editor.tabs.settings'),
            children: (
              <>
              <Card size="small">
                <Form layout="vertical" disabled={!canManage}>
                  <Form.Item label={t('meta.category')} style={{ marginBottom: 12 }}>
                    <Input
                      value={draft.category}
                      maxLength={COMPONENT_CONSTRAINTS.maxCategoryLength}
                      onChange={(e) => updateMeta({ category: e.target.value })}
                      data-testid="component-category"
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('meta.defaultStyle')}
                    help={t('meta.defaultStyleHint')}
                    style={{ marginBottom: 0 }}
                  >
                    <Input.TextArea
                      value={draft.defaultStyleJson}
                      autoSize={{ minRows: 2, maxRows: 5 }}
                      maxLength={COMPONENT_CONSTRAINTS.maxDefaultStyleLength}
                      placeholder={'{"backgroundColor":"#f7f9fc","padding":24}'}
                      onChange={(e) => updateMeta({ defaultStyleJson: e.target.value })}
                      data-testid="component-default-style"
                    />
                  </Form.Item>
                </Form>
              </Card>
              <ComponentUsagesPanel componentId={id} />
              </>
            ),
          },
        ]}
      />

      {versionsOpen ? (
        <ComponentVersionsDrawer
          open
          onClose={() => setVersionsOpen(false)}
          componentId={id}
          currentSnapshot={currentSnapshot}
          onRestored={() => void invalidateAll()}
        />
      ) : null}
    </div>
  );
}
